import ApiService from '@/common/api.service'

import * as utils from '@/common/utils'
import * as security from '@/common/security'
import store from '@/store/index'

const module = 'simCards/simCardsModule'

/**
 * Returns all properties and their default state.
 *
 * @return Object
 */
function properties () {
  return {
    id: null,
    cell_no: '',
    iccid: '',
    sim_card_provider_id: null,
    unit_id: null,
    imei: '',
    serial_no: '',
    reg_no: '',
    fleet_no: '',
    partner: {
      id: null,
      name: ''
    },
    agency: {
      id: null,
      name: ''
    },
    client: {
      id: null,
      name: ''
    },
    orgAssignMode: null, // can be null, 'client', 'agency', 'partner',
    note: '',
    customQueryParams: {}
  }
}

/**
 * Returns a copy of the default state.
 *
 * @return Object
 */
function defaults () {
  return {
    ...properties(),
    created_at: '',
    updated_at: ''
  }
}

function formDefaults () {
  return {
    partners: [],
    providers: []
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param simCard     Object
 *
 * @return Object
 */
function transformCreate (simCard) {
  return {
    id: simCard.id,
    cell_no: simCard.cell_no,
    iccid: simCard.iccid,
    sim_card_provider_id: simCard.sim_card_provider_id,
    unit_id: simCard.unit_id,
    partner_id: simCard.partner.id,
    agency_id: simCard.agency.id,
    client_id: simCard.client.id,
    note: simCard.note
  }
}

/**
 * Returns a new object containing a transformed object
 * that can be consumed by the backend API.
 *
 * @param simCard  Object
 *
 * @return Object
 */
function transformUpdate (simCard) {
  return {
    id: simCard.id,
    cell_no: simCard.cell_no,
    iccid: simCard.iccid,
    sim_card_provider_id: simCard.sim_card_provider_id,
    unit_id: simCard.unit_id,
    partner_id: simCard.partner.id,
    agency_id: simCard.agency.id,
    client_id: simCard.client.id,
    note: simCard.note
  }
}

/**
 * The module's state (VueX store object)
 */
export const state = {
  data: Object.assign({}, defaults()),
  form: Object.assign({}, formDefaults()),
  original: Object.assign({}, defaults()),
  isModified: false,
  isLoading: false
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @return Object
   */
  get (state) {
    return state.data
  },

  getOriginalUnitId (state) {
    return state.original.unit_id
  },

  /**
   * Returns the form's state object
   *
   * @return Object
   */
  getForm (state) {
    return state.form
  },

  /**
   * Returns true if the primary data have been modified
   * since it was originally loaded.
   *
   * @return Boolean
   */
  isModified (state) {
    return state.isModified
  },

  /**
   * Returns true if the data for the module is still being loaded.
   *
   * @return Boolean
   */
  isLoading (state) {
    return state.isLoading
  }
}

export const actions = {
  /**
   * Request a record from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetch (context, id) {
    return ApiService
      .get(`sim-cards/${id}`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request "create" form data from the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  fetchFormCreate (context) {
    return ApiService
      .get(`sim-cards/create`)
      .then((response) => {
        context.commit('set', defaults())
        context.commit('setFormData', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Request a record and "edit" form data from the API.
   *
   * @param context Object
   * @param id      Numeric
   *
   * @return Promise
   */
  fetchFormEdit (context, id) {
    return ApiService
      .get(`sim-cards/${id}/edit/`)
      .then((response) => {
        context.commit('set', response.data.data)
        context.commit('setFormData', response.data.data)
        context.commit('setOriginal', response.data.data)
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * POST (create) new record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  post (context) {
    let simCard = context.getters['get']

    return ApiService
      .post(`sim-cards`, transformCreate(simCard))
      .then((response) => {
        context.commit('resetGeneralOnly')
        context.commit('setIsModified', false)
        utils.addRecentHistory(module, response.data.data)
        return response
      })
  },

  /**
   * PUT (update) existing record through the API and upon success, resets all.
   *
   * @param context Object
   *
   * @return Promise
   */
  put (context) {
    const simCard = context.getters['get']

    return ApiService
      .put(`sim-cards/${simCard.id}`, transformUpdate(simCard))
      .then((response) => {
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * Un-assign a SIM card from the unit it is assigned to.
   *
   * @param context Object
   *
   * @return Promise
   */
  unassign (context) {
    const simCard = context.getters['get']

    return ApiService
      .put(`sim-cards/${simCard.id}/unassign`)
      .then((response) => {
        context.commit('setIsModified', false)
        return response
      })
  },

  /**
   * DELETE existing record through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  delete (context) {
    const id = context.getters['get'].id
    return ApiService
      .delete(`sim-cards/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * DELETE the unit from a sim card through the API.
   *
   * @param context Object
   *
   * @return Promise
   */
  deallocateUnit (context) {
    const id = context.getters['get'].id
    return ApiService
      .delete(`sim-cards/deallocate/${id}`)
      .then((response) => {
        return response
      })
  },

  /**
   * Queries (e.g. find) multiple records from the API.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    return ApiService
      .get(`sim-cards`, params)
      .then((response) => {
        return response
      })
  }
}

export const mutations = {
  /**
   * Set all the data properties according to the provided data.
   *
   * @param state Object
   * @param data  Object
   */
  set (state, data) {
    state.data.id = data.id
    state.data.cell_no = data.cell_no
    state.data.iccid = data.iccid
    state.data.sim_card_provider_id = data.sim_card_provider_id
    state.data.partner = data.partner
    state.data.agency = data.agency
    state.data.client = data.client
    state.data.unit_id = data.unit_id
    state.data.imei = data.imei
    state.data.serial_no = data.serial_no
    state.data.reg_no = data.reg_no
    state.data.fleet_no = data.fleet_no
    state.data.created_at = data.created_at
    state.data.updated_at = data.updated_at
    state.data.note = data.note
  },

  setFormData (state, data) {
    state.form.partners = data.partners
    state.form.providers = data.providers
  },

  /**
   * Sets the original primary data.
   *
   * @param state Object
   * @param data  Object
   */
  setOriginal (state, data) {
    state.original.cell_no = data.cell_no
    state.original.iccid = data.iccid
    state.original.sim_card_provider_id = data.sim_card_provider_id
    state.original.partner = data.partner
    state.original.agency = data.agency
    state.original.client = data.client
    state.original.unit_id = data.unit_id
    state.original.serial_no = data.serial_no
    state.original.reg_no = data.reg_no
    state.original.fleet_no = data.fleet_no
    state.original.note = data.note
  },

  /**
   * Set the unit's partner data.
   *
   * @param state
   * @param partner
   */
  setPartner (state, partner) {
    store.commit('units/unitsModule/clearAssignments')
    if (security.isAbovePartner()) {
      state.data.partner.id = partner.id
      state.data.partner.name = partner.name
      state.isModified = true
    }
  },

  /**
   * Set the unit's partner and agency data.
   *
   * @param state
   * @param agency
   */
  setAgency (state, agency) {
    store.commit('units/unitsModule/clearAssignments')
    if (security.isAbovePartner()) {
      state.data.partner.id = agency.partner.id
      state.data.partner.name = agency.partner.name
      state.isModified = true
    }
    if (security.isAboveAgency()) {
      state.data.agency.id = agency.id
      state.data.agency.name = agency.name
      state.isModified = true
    }
  },

  /**
   * Set the sim card's partner, agency and client id's.
   *
   * @param state
   * @param client
   */
  setClient (state, client) {
    store.commit('simCards/simCardsModule/clearClient')
    if (security.isAbovePartner()) {
      state.data.partner.id = client.partner.id
      state.data.partner.name = client.partner.name
    }
    if (security.isAboveAgency()) {
      state.data.agency.id = client.agency.id
      state.data.agency.name = client.agency.name
    }
    if (security.isAboveClient() || security.belongsToMultipleClients()) {
      state.data.client.id = client.id
      state.data.client.name = client.name
    }
  },

  /**
   * Clear the driver's partner agency and client data.
   *
   * @param state
   */
  clearClient (state) {
    state.data.partner.id = null
    state.data.partner.name = null
    state.data.agency.id = null
    state.data.agency.name = null
    state.data.client.id = null
    state.data.client.name = null
  },

  /**
   * Clear the unit's partner, agency and client data.
   *
   * @param state
   */
  clearAssignments (state) {
    state.data.partner.id = null
    state.data.partner.name = null
    state.data.agency.id = null
    state.data.agency.name = null
    state.data.client.id = null
    state.data.client.name = null
    state.isModified = true
  },

  /**
   * Sets the "modified" status to true or false
   *
   * @param state Object
   * @param isModified Boolean
   */
  setIsModified (state, isModified) {
    state.isModified = isModified
  },

  /**
   * Sets the "isLoading" status to true or false
   *
   * @param state Object
   * @param isLoading Boolean
   */
  setIsLoading (state, isLoading) {
    state.isLoading = isLoading
  },

  /**
   * Sets a specific property and value
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value String
   */
  setProp (state, { name, value }) {
    state.isModified = true
    utils.mutateProp(state.data, name, value)
  },

  setOrgAssignMode (state, { value }) {
    state.data.orgAssignMode = value
  },

  /**
   * Pushes a new element to an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param value mixed  The variable to push onto the array.
   */
  pushProp (state, { name, value }) {
    state.isModified = true
    state.data[name].push(value)
  },

  /**
   * removes an element from an array of a specific property.
   *
   * @param state Object
   * @param name  String Name of property (supports "." dot notation)
   * @param index Number The index of the element to remove
   */
  pullProp (state, { name, index }) {
    state.isModified = true
    utils.pullProp(state.data, name, index)
  },

  /**
   * Sets the values for multiple properties.
   *
   * @param state      Object
   * @param properties Array of objects in the form { name: 'propName', value: 'xyz' }
   */
  setProps (state, properties) {
    state.isModified = true
    properties.forEach(({ name, value }) => {
      utils.mutateProp(state.data, name, value)
    })
  },

  /**
   * Resets only the general fields.
   *
   * @param state
   */
  resetGeneralOnly (state) {
    state.data.cell_no = ''
    state.data.iccid = ''
    state.data.sim_card_provider_id = null
  },

  /**
   * Resets the state.
   *
   * @param state Object
   */
  reset (state) {
    state.data = defaults()
    state.original = properties()
    state.isModified = false
    state.isLoading = false
  },

  /**
   * Resets the form's state.
   *
   * @param state Object
   */
  resetForm (state) {
    state.form = formDefaults()
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/sim-cards/simCardsModule.js